import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { submitFeedback, type FeedbackRating } from '../lib/api';
import { cn } from '../lib/utils';

interface PlaygroundWeekFeedbackProps {
  /** ISO date of the week's earliest day — used as the week's anchor. */
  weekStart: string;
  /** The full week dict the agent emitted (frozen as workout_snapshot). */
  weekSnapshot: Record<string, unknown>;
  /** The synthetic profile the playground ran against. */
  profileSnapshot: Record<string, unknown>;
}

/**
 * Admin rating for a playground-generated week's overall structure
 * (day-to-day progression, intensity arc, exercise rotation).
 *
 * Self-contained — append-only, no store hydration. Each playground run
 * is a fresh sample, so re-rating the same week start in a second run
 * produces a new row (which is the correct behavior for post-training
 * data).
 */
export function PlaygroundWeekFeedback({
  weekStart,
  weekSnapshot,
  profileSnapshot,
}: PlaygroundWeekFeedbackProps) {
  const [draftRating, setDraftRating] = useState<FeedbackRating | null>(null);
  const [draftNotes, setDraftNotes] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRating, setSubmittedRating] = useState<FeedbackRating | null>(null);

  function pickRating(next: FeedbackRating) {
    setDraftRating(next);
    setExpanded(true);
  }

  async function handleSubmit() {
    if (!draftRating) return;
    setIsSubmitting(true);
    try {
      await submitFeedback({
        scope: 'week',
        workout_date: weekStart,
        rating: draftRating,
        notes: draftNotes.trim() || undefined,
        workout_snapshot: weekSnapshot,
        user_profile_snapshot: profileSnapshot,
      });
      toast.success('Weekly feedback saved');
      setSubmittedRating(draftRating);
      setExpanded(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save feedback';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleReset() {
    setDraftRating(null);
    setDraftNotes('');
    setExpanded(false);
    setSubmittedRating(null);
  }

  const activeRating = submittedRating ?? draftRating;

  return (
    <div className="rounded-md border-2 border-dashed bg-muted/30 px-3 py-2 my-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold">Rate weekly structure</p>
          <p className="text-[10px] text-muted-foreground">
            Day-to-day progression, intensity arc, exercise rotation.
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => pickRating('up')}
            disabled={isSubmitting}
            title="Good weekly structure"
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border transition',
              activeRating === 'up'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-300 bg-background text-gray-400 hover:border-gray-400 hover:text-gray-600',
            )}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => pickRating('down')}
            disabled={isSubmitting}
            title="Bad weekly structure"
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border transition',
              activeRating === 'down'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-300 bg-background text-gray-400 hover:border-gray-400 hover:text-gray-600',
            )}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {submittedRating && !expanded && (
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-[10px] text-muted-foreground">
            {submittedRating === 'up' ? '👍 saved' : '👎 saved'}
          </span>
          <button
            type="button"
            onClick={handleReset}
            className="text-[10px] text-muted-foreground hover:text-foreground"
          >
            Rate again
          </button>
        </div>
      )}

      {expanded && (
        <div className="mt-2 space-y-2">
          <textarea
            rows={2}
            placeholder={
              draftRating === 'up'
                ? "What's right? (correct phase, balanced split, exercise rotation hits each pattern…)"
                : draftRating === 'down'
                  ? "What's wrong? (too many CNS-heavy days back-to-back, no recovery between leg days, missing movement pattern…)"
                  : 'Pick 👍 or 👎 first.'
            }
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            disabled={isSubmitting || !draftRating}
            className="w-full resize-none rounded-md border bg-background p-2 text-[11px] focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary disabled:bg-muted"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setExpanded(false);
                if (!submittedRating) setDraftRating(null);
                setDraftNotes('');
              }}
              disabled={isSubmitting}
              className="px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!draftRating || isSubmitting}
              className={cn(
                'flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[10px] font-medium transition',
                draftRating && !isSubmitting
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {isSubmitting && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
